import PropTypes from 'prop-types';

import TextField from '@zenbusiness/zui/TextField';

/**
 * Number field using ZUI TextField with number type
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {number} props.value - Current number value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @returns {import('react').ReactElement} The number field component
 */
function NumberField({ def, value, onChange, disabled = false, error }) {
  const handleChange = (event) => {
    const newValue = event.target.value;

    // Convert to number if not empty, otherwise pass null
    if (newValue === '') {
      onChange(null);
    } else {
      const numValue = parseFloat(newValue);
      if (!Number.isNaN(numValue)) {
        onChange(numValue);
      }
    }
  };

  const fieldId = `number-${def.id}`;
  const hasError = Boolean(error);

  // Convert number to string for input
  const displayValue = value !== null && value !== undefined ? String(value) : '';

  return (
    <TextField
      disabled={disabled}
      error={hasError}
      fullWidth
      helperText={error || def.helpText}
      id={fieldId}
      inputProps={{
        step: def.step || 'any',
        min: def.validate?.min,
        max: def.validate?.max
      }}
      label={def.label}
      required={def.required}
      type="number"
      value={displayValue}
      variant="outlined"
      onChange={handleChange}
    />
  );
}

NumberField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['number']).isRequired,
    label: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    step: PropTypes.number,
    validate: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number
    })
  }).isRequired,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default NumberField;
